// The assistant's act → observe → continue loop (D34, docs/delivery/assistant-operator-plan.md §2), as pure decisions
// the conversation applies: which auto actions of a reply run (in order), and whether an automatic follow-up turn is
// sent after them. Bounded: at most ASSISTANT_MAX_FOLLOW_UPS follow-ups per user question, a Stop the user can press at
// any time, a confirm action pauses the loop until it is clicked, and an action that already failed with the same
// arguments in this question is never run again by itself.
import { ASSISTANT_MAX_FOLLOW_UPS, assistantServerActionClass, type AssistantTurnMeta } from "@maestros/contracts";
import type { AssistantAction, AssistantReply } from "../../api/types-assistant.ts";

export { ASSISTANT_MAX_FOLLOW_UPS };

/** One user question's loop: the follow-ups sent so far, Stop, and the actions that failed (by signature). */
export interface OperatorLoop {
  /** Follow-up turns already sent for this question (0 right after the user's own question). */
  rounds: number;
  stopped: boolean;
  failed: Set<string>;
}

export function newLoop(): OperatorLoop {
  return { rounds: 0, stopped: false, failed: new Set() };
}

/** Same type and same arguments: "the same action" for the no-repeat rule. */
export function actionSignature(action: Pick<AssistantAction, "type" | "args">): string {
  const args = action.args as Record<string, unknown>;
  return `${action.type}:${JSON.stringify(Object.keys(args).sort().map((k) => [k, args[k]]))}`;
}

export interface AutoPlan {
  /** Indexes to claim and run now, in reply order. */
  run: number[];
  /** Auto actions not run because the same action already failed in this question (left as cards). */
  repeats: number[];
}

/**
 * The auto actions of `reply` to run now: unclaimed, runnable here (`runnable` = the conversation's own checks: session,
 * route, never a refused one), and not a repeat of an action that already failed in this loop. A stopped loop runs
 * nothing.
 */
export function planAutoRun(reply: Pick<AssistantReply, "actions" | "claims">, loop: OperatorLoop, runnable: (action: AssistantAction) => boolean): AutoPlan {
  const plan: AutoPlan = { run: [], repeats: [] };
  if (loop.stopped) return plan;
  reply.actions.forEach((action, index) => {
    if (reply.claims[String(index)] || !runnable(action)) return;
    if (loop.failed.has(actionSignature(action))) plan.repeats.push(index);
    else plan.run.push(index);
  });
  return plan;
}

/** A confirm card still waiting for the user (not refused, not claimed, not dismissed here): the loop pauses on it. */
export function waitingForClick(reply: Pick<AssistantReply, "actions" | "claims">, dismissed: (index: number) => boolean): boolean {
  return reply.actions.some((a, i) => a.mode === "confirm" && a.refused === undefined && !reply.claims[String(i)] && !dismissed(i));
}

/**
 * D41-B2: on a turn the server executor runs (`auto_executor: "server"`), an action of the server's set is claimed and run
 * by the server, so this browser must not claim or run it.
 */
export function serverRunsAction(action: AssistantAction, turn: AssistantTurnMeta | null | undefined, home: boolean): boolean {
  return turn?.auto_executor === "server" && assistantServerActionClass(action, home) !== null;
}

/** D41-B2: the server sends the follow-up of a reply that has one of its own actions on a server turn; the browser must not. */
export function serverOwnsFollowUp(reply: Pick<AssistantReply, "actions">, turn: AssistantTurnMeta | null | undefined, home: boolean): boolean {
  return reply.actions.some((action) => serverRunsAction(action, turn, home));
}

export type LoopDecision = { kind: "follow_up"; round: number } | { kind: "stop"; why: "stopped" | "nothing_ran" | "waiting_for_click" | "limit" };

/**
 * After the actions of a reply ran (`ran` = how many were attempted now, by itself or by the click that resumes the
 * loop): continue with a follow-up turn, or stop and say why. A repeat that was skipped never counts as "ran".
 */
export function nextStep(loop: OperatorLoop, input: { ran: number; waitingForClick: boolean }): LoopDecision {
  if (loop.stopped) return { kind: "stop", why: "stopped" };
  if (input.ran === 0) return { kind: "stop", why: "nothing_ran" };
  if (input.waitingForClick) return { kind: "stop", why: "waiting_for_click" };
  if (loop.rounds >= ASSISTANT_MAX_FOLLOW_UPS) return { kind: "stop", why: "limit" };
  return { kind: "follow_up", round: loop.rounds + 1 };
}

/**
 * Runs the planned actions strictly in order (one at a time, each awaited), recording failures in the loop so they are
 * never repeated by themselves; a Stop pressed meanwhile ends the sequence before the next action. Returns how many ran.
 */
export async function runInOrder(
  reply: Pick<AssistantReply, "actions">,
  plan: AutoPlan,
  loop: OperatorLoop,
  run: (index: number, action: AssistantAction) => Promise<{ outcome: "succeeded" | "failed" } | "skipped">,
): Promise<number> {
  let ran = 0;
  for (const index of plan.run) {
    if (loop.stopped) break;
    const action = reply.actions[index];
    if (!action) continue;
    const result = await run(index, action);
    if (result === "skipped") continue;
    ran += 1;
    if (result.outcome === "failed") loop.failed.add(actionSignature(action));
  }
  return ran;
}
